// PanelAcceso.js — El armazón compartido de las pantallas de acceso: a un lado
// la barra de campaña y la tarjeta del formulario, al otro la imagen (y, si se
// quiere, un texto debajo). Lo usan la sección de login y la de registro, para
// que las dos pantallas se configuren con las mismas claves y se vean iguales.
//
// Claves de config (todas opcionales; los valores por defecto son los de la
// pantalla de HepCampus):
//   Sección   color_fondo… (buildBg), padding, ancho_maximo, gap,
//             alineacion_vertical, ancho_columna_form, ancho_columna_imagen
//   Barra     barra_texto, barra_fondo, barra_color, barra_radio, barra_padding,
//             barra_alineacion, barra_tamano, barra_peso, barra_fuente,
//             barra_espaciado, barra_margen
//   Tarjeta   card_fondo, card_radio, card_sombra, card_borde, card_padding,
//             card_padding_movil
//   Imágenes  imagen_lateral (+ imagen_lado, ancho_columna_imagen, alt_imagen,
//             ocultar_imagen_movil), imagen_aval (+ ancho_aval, margen_aval,
//             alineacion_aval, alt_aval)
//   Texto     texto_lateral (HTML) + color/tamaño/interlineado/peso/margen/alineación
import React from 'react';
import styled from 'styled-components';
import { BACKEND_BASE } from '../../../services/api';
import { buildBg } from '../bgHelpers';

const Wrapper = styled.section`
  width: 100%;
  padding: ${p => p.$padding || '80px 2rem 110px'};
  background: ${p => p.$bg || '#f4f4f4'};
`;

const Inner = styled.div`
  max-width: ${p => p.$maxWidth || '1200px'};
  margin: 0 auto;
  display: flex;
  flex-direction: ${p => p.$imagenIzquierda ? 'row-reverse' : 'row'};
  align-items: ${p => p.$align || 'flex-start'};
  justify-content: space-between;
  gap: ${p => p.$gap || '40px'};
  @media (max-width: 900px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ColForm = styled.div`
  width: ${p => p.$ancho || '567px'};
  max-width: 100%;
`;

const ColLateral = styled.div`
  width: ${p => p.$ancho || '536px'};
  max-width: 100%;
  ${p => p.$ocultarMovil && '@media (max-width: 900px) { display: none; }'}
  img { width: 100%; height: auto; display: block; }
`;

const Barra = styled.div`
  background: ${p => p.$bg || '#21315c'};
  color: ${p => p.$color || '#ffffff'};
  border-radius: ${p => p.$radio || '999px'};
  padding: ${p => p.$padding || '10px 24px'};
  text-align: ${p => p.$align || 'center'};
  font-size: ${p => p.$size || '20px'};
  font-weight: ${p => p.$weight || 400};
  font-family: ${p => p.$font || 'inherit'};
  letter-spacing: ${p => p.$spacing || 'normal'};
  margin: ${p => p.$margin || '0 0 24px'};
`;

const Card = styled.div`
  background: ${p => p.$bg || '#ffffff'};
  border-radius: ${p => p.$radio || '20px'};
  box-shadow: ${p => p.$sombra || '0 6px 24px rgba(15,23,42,0.08)'};
  ${p => p.$borde ? `border: ${p.$borde};` : ''}
  padding: ${p => p.$padding || '48px 56px'};
  @media (max-width: 600px) { padding: ${p => p.$paddingMovil || '32px 24px'}; }
`;

const Aval = styled.div`
  margin: ${p => p.$margin || '40px 0 0'};
  text-align: ${p => p.$align || 'center'};
  img { width: ${p => p.$ancho || '250px'}; max-width: 100%; height: auto; display: inline-block; }
`;

const TextoLateral = styled.div`
  margin: ${p => p.$margin || '30px 0 0'};
  text-align: ${p => p.$align || 'center'};
  color: ${p => p.$color || '#21315c'};
  font-size: ${p => p.$size || '30px'};
  font-weight: ${p => p.$weight || 400};
  line-height: ${p => p.$lineHeight || '1.25'};
  strong { font-weight: 700; }
  @media (max-width: 600px) { font-size: ${p => p.$sizeMobile || '22px'}; }
`;

export const resolverImagen = (v) => {
  if (!v) return null;
  return v.startsWith('http') ? v : `${BACKEND_BASE}/${v.replace(/^\//, '')}`;
};

const PanelAcceso = ({ config, tl, tarjeta = true, children }) => {
  const c = config || {};
  const t = (v, def = '') => (tl ? tl(v, def) : (v || def));

  const imagenLateral = resolverImagen(c.imagen_lateral);
  const imagenAval = resolverImagen(c.imagen_aval);
  const textoLateral = t(c.texto_lateral);

  const formulario = tarjeta ? (
    <Card
      $bg={c.card_fondo}
      $radio={c.card_radio}
      $sombra={c.card_sombra}
      $borde={c.card_borde}
      $padding={c.card_padding}
      $paddingMovil={c.card_padding_movil}
    >
      {children}
    </Card>
  ) : children;

  return (
    <Wrapper $bg={buildBg(c, '#f4f4f4')} $padding={c.padding}>
      <Inner
        $maxWidth={c.ancho_maximo}
        $gap={c.gap}
        $align={c.alineacion_vertical}
        $imagenIzquierda={c.imagen_lado === 'izquierda'}
      >
        <ColForm $ancho={c.ancho_columna_form}>
          {t(c.barra_texto) && (
            <Barra
              $bg={c.barra_fondo}
              $color={c.barra_color}
              $radio={c.barra_radio}
              $padding={c.barra_padding}
              $align={c.barra_alineacion}
              $size={c.barra_tamano}
              $weight={c.barra_peso}
              $font={c.barra_fuente}
              $spacing={c.barra_espaciado}
              $margin={c.barra_margen}
              dangerouslySetInnerHTML={{ __html: t(c.barra_texto) }}
            />
          )}
          {formulario}
          {imagenAval && (
            <Aval $margin={c.margen_aval} $align={c.alineacion_aval} $ancho={c.ancho_aval}>
              <img src={imagenAval} alt={t(c.alt_aval)} />
            </Aval>
          )}
        </ColForm>

        {(imagenLateral || textoLateral) && (
          <ColLateral $ancho={c.ancho_columna_imagen} $ocultarMovil={c.ocultar_imagen_movil === true}>
            {imagenLateral && <img src={imagenLateral} alt={t(c.alt_imagen)} />}
            {textoLateral && (
              <TextoLateral
                $margin={c.margen_texto_lateral}
                $align={c.alineacion_texto_lateral}
                $color={c.color_texto_lateral}
                $size={c.tamano_texto_lateral}
                $sizeMobile={c.tamano_texto_lateral_movil}
                $weight={c.peso_texto_lateral}
                $lineHeight={c.interlineado_texto_lateral}
                dangerouslySetInnerHTML={{ __html: textoLateral }}
              />
            )}
          </ColLateral>
        )}
      </Inner>
    </Wrapper>
  );
};

export default PanelAcceso;
